import { AdminPermissions, SoftwareSeatPermissions } from '@zspace/roles';
import {
  AdminLicenseKeyExpandableTableRow,
  AdminLicenseKeyTableRow,
  Criteria,
  DeferredResponse,
  PaginatedAPIResponse,
  SalesOrder,
  SortDirection,
} from '@zspace/types';
import { Suspense, useCallback, useMemo, useState } from 'react';
import {
  defer,
  LoaderFunction,
  useAsyncValue,
  useLoaderData,
} from 'react-router-dom';
import { AdminProvider } from '../../shared/context/admin-context';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import useHttpRequest from '../../shared/hooks/http-request';
import ProtectedPage from '../../shared/protected-page/protected-page';
import BoxLayout from '../../ui/box-layout/box-layout';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import { ExpandableRow } from '../../ui/table/types';
import AdminHeader from '../admin-header/admin-header';
import AdminInboxTable, {
  AdminToolsView,
} from '../admin-inbox-table/admin-inbox-table';
import {
  fetchLicenseKeysBySalesOrderDownloadableData,
  fetchLicenseKeysSalesOrders as fetchLicenseKeysSalesOrdersRequest,
} from '../admin-service';
import { adminLicenseKeyTableColumns as adminLicenseKeyColumns } from '../admin-table/admin-table-columns';

const initialCriteria: Criteria = {
  itemsPerPage: 10,
  pageNumber: 1,
  search: '',
  sortBy: '',
  sortDirection: SortDirection.ASC,
};

const FETCH_ADMIN_LICENSE_KEYS_SALES_ORDERS_ERROR_MESSAGE =
  'License keys sales orders could not be fetched. Please try again';
const FETCH_ADMIN_LICENSE_KEYS_BY_SALES_ORDER_ERROR_MESSAGE =
  'License keys could not be fetched. Please try again';
const DEFAULT_INBOX_TABLE_TEXT =
  'Please select a sales order to display license keys';
const TABLE_EMPTY_TEXT = 'There are no license keys at the moment';
const INBOX_EMPTY_TEXT = 'There are no sales orders at the moment';
const EXPORT_TOOLTIP_MESSAGE = 'Please select a Sales Order to export';

export const loader: LoaderFunction = () => {
  const response = fetchLicenseKeysSalesOrdersRequest(initialCriteria);

  return defer({ response });
};

export function AdminLicenseKeysPageContent() {
  const licenseKeysSalesOrdersInitialResponse =
    useAsyncValue() as PaginatedAPIResponse<SalesOrder>;
  const [licenseKeySalesOrders, setLicenseKeySalesOrders] = useState<
    SalesOrder[]
  >(licenseKeysSalesOrdersInitialResponse.data);
  const [
    licenseKeysSalesOrdersDataResponse,
    setLicenseKeysSalesOrdersDataResponse,
  ] = useState<PaginatedAPIResponse<SalesOrder>>(
    licenseKeysSalesOrdersInitialResponse
  );
  const [licenseKeyCriteria, setLicenseKeyCriteria] =
    useState<Criteria>(initialCriteria);
  const {
    executeHttpRequest: executeFetchLicenseKeysSalesOrdersRequest,
    isLoading: loadingInitialSalesOrders,
  } = useHttpRequest();
  const { executeHttpRequest: executeFetchMoreLicenseKeysSalesOrdersRequest } =
    useHttpRequest();

  const hasMoreLicenseKeysSalesOrders = useMemo(
    () => !!licenseKeysSalesOrdersDataResponse.hasMore,
    [licenseKeysSalesOrdersDataResponse.hasMore]
  );

  const fetchLicenseKeysSalesOrders = useCallback(
    async (criteria: Criteria) =>
      executeFetchLicenseKeysSalesOrdersRequest({
        asyncFunction: async () => {
          const response = await fetchLicenseKeysSalesOrdersRequest(criteria);
          setLicenseKeySalesOrders(response.data);
          setLicenseKeysSalesOrdersDataResponse(response);
        },
        customErrorMessage: FETCH_ADMIN_LICENSE_KEYS_SALES_ORDERS_ERROR_MESSAGE,
      }),
    [executeFetchLicenseKeysSalesOrdersRequest]
  );

  const fetchMoreLicenseKeysSalesOrders = useCallback(
    () =>
      executeFetchMoreLicenseKeysSalesOrdersRequest({
        asyncFunction: async () => {
          const nextPage = licenseKeyCriteria.pageNumber + 1;
          const updatedCriteria = {
            ...licenseKeyCriteria,
            pageNumber: nextPage,
          };
          const response = await fetchLicenseKeysSalesOrdersRequest(
            updatedCriteria
          );
          setLicenseKeyCriteria(updatedCriteria);
          setLicenseKeySalesOrders((prevLicenseKeysSalesOrders) =>
            prevLicenseKeysSalesOrders.concat(response.data)
          );
          setLicenseKeysSalesOrdersDataResponse(response);
        },
        customErrorMessage: FETCH_ADMIN_LICENSE_KEYS_SALES_ORDERS_ERROR_MESSAGE,
      }),
    [executeFetchMoreLicenseKeysSalesOrdersRequest, licenseKeyCriteria]
  );

  const handleTableFilterSubmit = useCallback(
    async (updatedLicenseKeysSalesOrderCriteria: Criteria) => {
      await fetchLicenseKeysSalesOrders(updatedLicenseKeysSalesOrderCriteria);
    },
    [fetchLicenseKeysSalesOrders]
  );

  const onSearchChange = useCallback(
    (search: string) => {
      const updatedCriteria = {
        ...initialCriteria,
        search,
      };
      setLicenseKeyCriteria(updatedCriteria);
      handleTableFilterSubmit(updatedCriteria);
    },
    [handleTableFilterSubmit]
  );

  const isExpandableTableRow = useCallback(
    (row: AdminLicenseKeyTableRow) => !!row.keys?.length,
    []
  );

  const adminLicenseKeyTableColumns = useMemo(
    () => adminLicenseKeyColumns(isExpandableTableRow),
    [isExpandableTableRow]
  );

  const expandableRow = useMemo(
    () =>
      ({
        expandable: (row: AdminLicenseKeyTableRow) => isExpandableTableRow(row),
        expandableRowIterator: (row: AdminLicenseKeyTableRow) => {
          return row.keys;
        },
      } as ExpandableRow<
        AdminLicenseKeyTableRow,
        AdminLicenseKeyExpandableTableRow
      >),
    [isExpandableTableRow]
  );

  return (
    <BoxLayout
      className="is-min-height-80 mx-10 my-4 mb-4"
      header={
        <AdminHeader
          title="License Keys"
          search={licenseKeyCriteria.search}
          onSearchChange={onSearchChange}
          onExport={fetchLicenseKeysBySalesOrderDownloadableData}
          requiredPermissions={[
            SoftwareSeatPermissions.SOFTWARE_SEATS_DETAILS_EXPORT,
          ]}
        />
      }
    >
      <AdminInboxTable
        fetchMoreInboxItems={fetchMoreLicenseKeysSalesOrders}
        hasMoreInboxItems={hasMoreLicenseKeysSalesOrders}
        inboxItems={licenseKeySalesOrders}
        loadingInitialInboxItems={loadingInitialSalesOrders}
        tableColumns={adminLicenseKeyTableColumns}
        searchValue={licenseKeyCriteria.search}
        expandableRow={expandableRow}
        initialCriteriaConfig={{
          sortBy: 'softwareTitle',
          sortDirection: SortDirection.ASC,
        }}
      />
    </BoxLayout>
  );
}

export function AdminLicenseKeysPage() {
  const { response } = useLoaderData() as DeferredResponse<
    Promise<PaginatedAPIResponse<SalesOrder>>
  >;

  return (
    <ProtectedPage permissions={AdminPermissions.ADMIN_TOOLS_ACCESS}>
      <Suspense fallback={<PageSpinner />}>
        <ErrorHandlingAwait resolve={response}>
          <AdminProvider
            adminView={AdminToolsView.LICENSE_KEYS}
            fetchTableRecordsErrorMessage={
              FETCH_ADMIN_LICENSE_KEYS_BY_SALES_ORDER_ERROR_MESSAGE
            }
            defaultInboxTableText={DEFAULT_INBOX_TABLE_TEXT}
            tableEmptyText={TABLE_EMPTY_TEXT}
            inboxEmptyText={INBOX_EMPTY_TEXT}
            exportTooltipMessage={EXPORT_TOOLTIP_MESSAGE}
          >
            <AdminLicenseKeysPageContent />
          </AdminProvider>
        </ErrorHandlingAwait>
      </Suspense>
    </ProtectedPage>
  );
}

export default AdminLicenseKeysPage;
